<script setup>
import { ref, computed, onMounted } from 'vue';
import KnowledgeBaseAPI from 'dashboard/api/knowledgeBase';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const isLoading = ref(true);
const stats = ref({});
const events = ref([]);
const filterKind = ref('all');

const qualityFilterOptions = [
  { value: 'all', label: '全部记录' },
  { value: 'reply', label: '仅 AI 回答' },
  { value: 'handoff', label: '仅转人工' },
];

const loadData = async () => {
  isLoading.value = true;
  try {
    const [sRes, eRes] = await Promise.all([
      KnowledgeBaseAPI.getStats(),
      KnowledgeBaseAPI.getAiEvents(),
    ]);
    stats.value = sRes.data || {};
    events.value = eRes.data?.events || [];
  } catch (error) {
    useAlert(error.message || '加载质检数据失败');
  } finally {
    isLoading.value = false;
  }
};

const aiRate = computed(() => {
  const total = stats.value.total_resolved || 0;
  const ai = stats.value.ai_resolved || 0;
  return total ? Math.round((ai / total) * 100) : 0;
});

const accuracy = computed(() => stats.value.ai_accuracy || {});

const filteredEvents = computed(() => {
  if (filterKind.value === 'reply') return events.value.filter(e => e.kind === 'reply');
  if (filterKind.value === 'handoff') return events.value.filter(e => e.kind === 'handoff');
  return events.value;
});

const labelEvent = async (conversationId, target, verdict) => {
  try {
    await KnowledgeBaseAPI.submitFeedback({
      conversation_id: conversationId,
      target,
      verdict,
    });
    useAlert('标注已记录 ✓');
    await loadData();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '标注失败');
  }
};

const formatTime = (ts) => {
  if (!ts) return '—';
  return new Date(Number(ts)).toLocaleString('zh-CN', { hour12: false });
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 指标卡片网格 -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="p-4 border rounded-xl bg-n-solid-2 border-n-weak">
        <span class="text-xs text-n-slate-10 font-medium">AI 解决率</span>
        <div class="text-2xl font-bold text-n-brand mt-1">{{ aiRate }}%</div>
        <span class="text-xs text-n-slate-10 mt-1 block">AI 解决 {{ stats.ai_resolved || 0 }} / 总 {{ stats.total_resolved || 0 }}</span>
      </div>

      <div class="p-4 border rounded-xl bg-n-solid-2 border-n-weak">
        <span class="text-xs text-n-slate-10 font-medium">AI 回复总数</span>
        <div class="text-2xl font-bold text-n-slate-12 mt-1">{{ stats.ai_replies || 0 }}</div>
        <span class="text-xs text-n-slate-10 mt-1 block">人工接管 {{ stats.human_resolved || 0 }}</span>
      </div>

      <div class="p-4 border rounded-xl bg-n-solid-2 border-n-weak">
        <span class="text-xs text-n-slate-10 font-medium">误答率（人工复核）</span>
        <div class="text-2xl font-bold text-n-slate-12 mt-1">
          {{ accuracy.misanswer_rate !== null && accuracy.misanswer_rate !== undefined ? (Math.round(accuracy.misanswer_rate * 100) + '%') : '—' }}
        </div>
        <span class="text-xs text-n-ruby-11 mt-1 block">{{ accuracy.marked_wrong || 0 }} 条标记答错</span>
      </div>

      <div class="p-4 border rounded-xl bg-n-solid-2 border-n-weak">
        <span class="text-xs text-n-slate-10 font-medium">转人工准确率</span>
        <div class="text-2xl font-bold text-n-slate-12 mt-1">
          {{ accuracy.handoff_precision !== null && accuracy.handoff_precision !== undefined ? (Math.round(accuracy.handoff_precision * 100) + '%') : '—' }}
        </div>
        <span class="text-xs text-n-slate-10 mt-1 block">该转 {{ accuracy.handoff_marked_correct || 0 }} / 不该转 {{ accuracy.handoff_marked_wrong || 0 }}</span>
      </div>
    </div>

    <!-- 质检列表 -->
    <div class="p-6 border rounded-xl bg-n-solid-2 border-n-weak flex flex-col gap-4">
      <div class="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">AI 回答与转人工质检台账</h3>
          <p class="text-xs text-n-slate-10 mt-0.5">逐条复核 AI 回复与转人工原因，坐席标注将实时回流上方质量看板。</p>
        </div>
        <div class="flex items-center gap-2">
          <Select
            v-model="filterKind"
            :options="qualityFilterOptions"
            aria-label="记录类型筛选"
          />
          <Button
            label="刷新"
            size="sm"
            variant="outline"
            color="slate"
            @click="loadData"
          />
        </div>
      </div>

      <!-- 质检表格 -->
      <div class="border rounded-lg border-n-weak overflow-hidden">
        <table class="w-full text-left text-sm">
          <thead class="bg-n-alpha-1 text-xs font-medium text-n-slate-11 border-b border-n-weak">
            <tr>
              <th class="px-4 py-2.5">时间</th>
              <th class="px-4 py-2.5 w-24">类型</th>
              <th class="px-4 py-2.5">AI 回复 / 转人工原因</th>
              <th class="px-4 py-2.5 w-32">当前标注</th>
              <th class="px-4 py-2.5 text-right w-36">复核操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-if="filteredEvents.length === 0">
              <td colspan="5" class="px-4 py-8 text-center text-xs text-n-slate-10">
                暂无质检记录 —— 有访客咨询后此处将出现待复核的条目
              </td>
            </tr>
            <tr v-for="e in filteredEvents" :key="e.id" class="hover:bg-n-alpha-1 transition-colors">
              <td class="px-4 py-3 text-xs text-n-slate-10 whitespace-nowrap">
                {{ formatTime(e.created_at) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="e.kind === 'reply' ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'"
                >
                  {{ e.kind === 'reply' ? 'AI 回复' : '转人工' }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-n-slate-11 leading-relaxed">
                {{ e.content || (e.kind === 'handoff' ? '置信度不足或敏感词命中，升级人工' : '—') }}
              </td>
              <td class="px-4 py-3 text-xs whitespace-nowrap">
                <span v-if="e.verdict === 'correct'" class="text-emerald-600 dark:text-emerald-400 font-medium">✓ 标为正确</span>
                <span v-else-if="e.verdict === 'wrong'" class="text-n-ruby-11 font-medium">✗ 标为错误</span>
                <span v-else class="text-n-slate-9">未复核</span>
              </td>
              <td class="px-4 py-3 text-right whitespace-nowrap">
                <div class="inline-flex gap-1.5 justify-end">
                  <Button
                    label="答对"
                    size="xs"
                    variant="outline"
                    color="teal"
                    @click="labelEvent(e.conversation_id, e.kind === 'reply' ? 'reply' : 'handoff', 'correct')"
                  />
                  <Button
                    label="答错"
                    size="xs"
                    variant="outline"
                    color="ruby"
                    @click="labelEvent(e.conversation_id, e.kind === 'reply' ? 'reply' : 'handoff', 'wrong')"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
